import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  formatFamilyTreeDataFromObituaryReport,
  AddToFamilyTreeButton,
} from '@ltvco/refresh-lib/fmt';
import { reportGetters } from '@ltvco/refresh-lib/v1';
import { Person } from '@mui/icons-material';
import { useCallback, useContext } from 'react';

import { constants } from 'appConstants';
import { useSearchPersonResultsContext } from 'routes/SearchPerson/context';
import { useQueryParams } from 'utils/useQueryParams';

import { BaseCard } from '../BaseCard';
import {
  PersonIconWrapper,
  NameTypography,
  SecondaryTextTypography,
  InfoSection,
} from '../common/components';
import { ObituaryCardData } from '../common/types';
import { formatName, formatShorthandDate } from '../common/utils';

export function MobileObituaryCard({
  id,
  name,
  dob,
  dod,
  city,
  state,
  handleAddToFamilyTree,
  reportInTree,
  isCreatingFamilyTree,
  obituaryData,
  isExpandable = false,
}: ObituaryCardData) {
  const queryParams = useQueryParams();
  const { isChatPage, handleObituaryReport } = useSearchPersonResultsContext();
  const { trackEventGA4 } = useContext(AppConfig);

  const handleOpenObituaryReport = async () => {
    queryParams.set('id', id || '');
    const response = await reportGetters.obituary({ queryParams });
    const permalink = response?.report?.permalink;

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'primary cta',
      interacted_from: 'Obituary Records',
    });

    trackEventGA4({
      event_name: 'viewed_report',
      interacted_from: 'search_results',
      report_type: 'obituary',
    });

    if (isChatPage) {
      if (permalink) {
        handleObituaryReport?.(permalink);
      }
      return;
    }

    const windowObjectReference = window.open();

    if (!permalink) {
      if (windowObjectReference) {
        windowObjectReference.close();
      }

      return;
    }

    // It's possible for the browser to block opening a new window, in which case windowObjectReference will be null.
    // In that case, we just redirect the current window.
    const windowTarget = windowObjectReference || window;
    windowTarget.location.href = `${constants.links.baseUrl}/report/obituary?permalink=${permalink}`;
  };

  const onHandleAddToFamilyTree = useCallback(() => {
    if (!obituaryData) return;

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'fam tree cta',
      interacted_from: 'Obituary Records',
    });

    const postIndi = formatFamilyTreeDataFromObituaryReport(
      id!,
      name,
      dob,
      dod,
      city,
      state,
      null,
      null
    );
    if (!postIndi) return;

    handleAddToFamilyTree(postIndi);
  }, [obituaryData, handleAddToFamilyTree, id, name, dob, dod, city, state]);

  const formattedName = formatName(name || '');

  return (
    <BaseCard
      name={name}
      isExpandable={isExpandable}
      onDetailsClick={handleOpenObituaryReport}
      renderIcon={() => (
        <PersonIconWrapper>
          <Person color="primary" fontSize="small" />
        </PersonIconWrapper>
      )}
      renderHeader={() => (
        <>
          <NameTypography variant="subtitle1" fontWeight="bold">
            {formattedName}
          </NameTypography>
          <SecondaryTextTypography variant="body2">
            Born: {formatShorthandDate(dob) || '-'}
          </SecondaryTextTypography>
          <SecondaryTextTypography variant="body2">
            Death: {formatShorthandDate(dod, true) || '-'}
          </SecondaryTextTypography>
          {city && state && (
            <SecondaryTextTypography variant="body2">
              {city}, {state}
            </SecondaryTextTypography>
          )}
        </>
      )}
      renderExpandedContent={() => (
        <InfoSection>
          <AddToFamilyTreeButton
            variant="pill-outline"
            reportInFamilyTree={reportInTree}
            addRelativeToFamilyTree={onHandleAddToFamilyTree}
            isCreatingFamilyTree={isCreatingFamilyTree}
            sx={{ mb: 0 }}
          />
        </InfoSection>
      )}
      footerText={formattedName}
    />
  );
}
